import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  MobileStepper,
  Fade,
} from '@mui/material';
import {
  Close as CloseIcon,
  KeyboardArrowRight,
  KeyboardArrowLeft,
  Dashboard as DashboardIcon,
  FileOpen as PermitIcon,
  Add as AddIcon,
  Search as SearchIcon,
  People as UsersIcon,
  AccountCircle as ProfileIcon,
  CheckCircle as DoneIcon,
  Waving as WaveIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const STORAGE_KEY = 'pms_onboarding_done';

// title/description are looked up via `onboarding.stepNTitle` / `stepNDesc`
const steps = [
  {
    icon: <DashboardIcon sx={{ fontSize: 64, color: '#1a3c6e' }} />,
    key: 'step1',
    color: '#e8f0fe',
    accent: '#1a3c6e',
  },
  {
    icon: <DashboardIcon sx={{ fontSize: 64, color: '#0d6efd' }} />,
    key: 'step2',
    color: '#e8f4fd',
    accent: '#0d6efd',
  },
  {
    icon: <PermitIcon sx={{ fontSize: 64, color: '#198754' }} />,
    key: 'step3',
    color: '#e8f8f0',
    accent: '#198754',
  },
  {
    icon: <AddIcon sx={{ fontSize: 64, color: '#6f42c1' }} />,
    key: 'step4',
    color: '#f3ecff',
    accent: '#6f42c1',
  },
  {
    icon: <SearchIcon sx={{ fontSize: 64, color: '#fd7e14' }} />,
    key: 'step5',
    color: '#fff4e8',
    accent: '#fd7e14',
  },
  {
    icon: <UsersIcon sx={{ fontSize: 64, color: '#dc3545' }} />,
    key: 'step6',
    color: '#fdeaea',
    accent: '#dc3545',
    adminOnly: true,
  },
  {
    icon: <ProfileIcon sx={{ fontSize: 64, color: '#20c997' }} />,
    key: 'step7',
    color: '#e6faf5',
    accent: '#20c997',
  },
  {
    icon: <DoneIcon sx={{ fontSize: 64, color: '#1a3c6e' }} />,
    key: 'step8',
    color: '#e8f0fe',
    accent: '#1a3c6e',
    isLast: true,
  },
];

export function hasSeenOnboarding() {
  return localStorage.getItem(STORAGE_KEY) === 'true';
}

export function resetOnboarding() {
  localStorage.removeItem(STORAGE_KEY);
}

export default function OnboardingTutorial({ open, onClose, isAdmin = false }) {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);

  const visibleSteps = steps.filter((s) => !s.adminOnly || isAdmin);
  const maxSteps = visibleSteps.length;
  const current = visibleSteps[activeStep];

  const handleNext = () => {
    if (activeStep === maxSteps - 1) {
      handleFinish();
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => setActiveStep((prev) => prev - 1);

  const handleFinish = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setActiveStep(0);
    onClose();
  };

  const handleSkip = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setActiveStep(0);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleSkip}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Fade}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: '0 24px 60px rgba(0,0,0,0.18)',
        },
      }}
    >
      {/* Coloured top banner */}
      <Box
        sx={{
          backgroundColor: current.accent,
          py: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          position: 'relative',
          transition: 'background-color 0.4s ease',
        }}
      >
        <IconButton
          onClick={handleSkip}
          size="small"
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            color: 'rgba(255,255,255,0.8)',
            '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.15)' },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        {/* Step number badge */}
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            left: 16,
            backgroundColor: 'rgba(255,255,255,0.2)',
            borderRadius: 10,
            px: 1.5,
            py: 0.3,
          }}
        >
          <Typography variant="caption" sx={{ color: '#fff', fontWeight: 600 }}>
            {activeStep + 1} / {maxSteps}
          </Typography>
        </Box>

        <Box
          sx={{
            backgroundColor: 'rgba(255,255,255,0.15)',
            borderRadius: '50%',
            p: 2.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {React.cloneElement(current.icon, { sx: { fontSize: 56, color: '#fff' } })}
        </Box>
      </Box>

      <DialogContent sx={{ px: 4, pt: 3, pb: 1 }}>
        <Typography
          variant="h6"
          align="center"
          sx={{ fontWeight: 700, color: current.accent, mb: 1.5, transition: 'color 0.3s' }}
        >
          {t(`onboarding.${current.key}Title`)}
        </Typography>
        <Typography
          variant="body2"
          align="center"
          sx={{ color: '#555', lineHeight: 1.8, minHeight: 72 }}
        >
          {t(`onboarding.${current.key}Desc`)}
        </Typography>
      </DialogContent>

      {/* Dot stepper + nav buttons */}
      <Box sx={{ px: 3, pb: 3, pt: 1 }}>
        <MobileStepper
          variant="dots"
          steps={maxSteps}
          position="static"
          activeStep={activeStep}
          sx={{
            backgroundColor: 'transparent',
            '& .MuiMobileStepper-dot': { width: 8, height: 8 },
            '& .MuiMobileStepper-dotActive': { backgroundColor: current.accent },
          }}
          backButton={
            <Button
              size="small"
              onClick={handleBack}
              disabled={activeStep === 0}
              startIcon={<KeyboardArrowLeft className="rtl-flip-icon" />}
              sx={{ textTransform: 'none', color: '#666' }}
            >
              {t('common.back')}
            </Button>
          }
          nextButton={
            <Button
              size="small"
              onClick={handleNext}
              variant="contained"
              endIcon={current.isLast ? <DoneIcon /> : <KeyboardArrowRight className="rtl-flip-icon" />}
              sx={{
                textTransform: 'none',
                backgroundColor: current.accent,
                '&:hover': { backgroundColor: current.accent, filter: 'brightness(0.9)' },
                borderRadius: 2,
                px: 2,
              }}
            >
              {current.isLast ? t('onboarding.getStarted') : t('onboarding.next')}
            </Button>
          }
        />

        {!current.isLast && (
          <Box textAlign="center" mt={0.5}>
            <Button
              size="small"
              onClick={handleSkip}
              sx={{ textTransform: 'none', color: '#aaa', fontSize: 12 }}
            >
              {t('onboarding.skipTutorial')}
            </Button>
          </Box>
        )}
      </Box>
    </Dialog>
  );
}
